import { ChartContainer } from '@lms/ui/chart';
import { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';
import { useAccentColor } from '../brand';
import { formatCompact } from '../format';
import { OTHER_COLOR, type PivotRow, type Series } from '../series';
import {
  axisTick,
  ChartEmpty,
  ChartLegendList,
  MAX_BAR_SIZE,
  SeriesTooltip,
} from './parts';

export interface RatingEntry {
  answer: number;
  before: number;
  after: number;
}

/**
 * Answer distribution before vs. after a course, as grouped columns over the
 * full rating scale (1…scale), so unused answers still show as zero.
 */
export function RatingChart({
  data,
  scale = 5,
  labels = { before: 'Before course', after: 'After course' },
  locale,
  height = 220,
  emptyLabel = 'No answers for this period',
}: {
  data: readonly RatingEntry[];
  scale?: number;
  labels?: { before: string; after: string };
  locale?: string;
  height?: number;
  emptyLabel?: string;
}) {
  const accent = useAccentColor();
  const series: Series[] = [
    { key: 'before', name: labels.before, color: OTHER_COLOR },
    { key: 'after', name: labels.after, color: accent },
  ];

  const rows = useMemo<PivotRow[]>(() => {
    const max = Math.max(scale, ...data.map((d) => d.answer));
    return Array.from({ length: max }, (_, i) => {
      const hit = data.find((d) => d.answer === i + 1);
      const before = hit?.before ?? 0;
      const after = hit?.after ?? 0;
      return { category: String(i + 1), before, after, total: before + after };
    });
  }, [data, scale]);

  if (!rows.some((r) => r.total > 0)) {
    return <ChartEmpty label={emptyLabel} height={height} />;
  }

  return (
    <div className="flex flex-col gap-3">
      <ChartContainer
        config={Object.fromEntries(
          series.map((s) => [s.key, { label: s.name, color: s.color }]),
        )}
        className="aspect-auto w-full"
        style={{ height }}
      >
        <BarChart data={rows} barGap={2} accessibilityLayer>
          <CartesianGrid vertical={false} />
          <XAxis
            dataKey="category"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tick={axisTick}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            allowDecimals={false}
            width={36}
            tickCount={5}
            tick={axisTick}
            tickFormatter={(v: number) => formatCompact(v, locale)}
          />
          <Tooltip
            cursor={{ fill: 'var(--muted)', opacity: 0.6 }}
            content={
              <SeriesTooltip
                series={series}
                locale={locale}
                title={(row) => row.category}
              />
            }
          />
          {series.map((s) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              fill={s.color}
              radius={[4, 4, 0, 0]}
              maxBarSize={MAX_BAR_SIZE}
              isAnimationActive={false}
            />
          ))}
        </BarChart>
      </ChartContainer>
      <ChartLegendList series={series} />
    </div>
  );
}
